import React from 'react';
import { Tooltip } from '@gerdau/hefesto';
import {
  ProgressVolumeBarContainer,
  ProgressVolumeBarWrapper,
  RequestedQtyText
} from './styles';
import { EMeasureUnit } from '../../../../store/commomTypes';

const formatMeasureUnit = (unit?: string) => {
  if (!unit) return unit;
  const upperUnit = unit.toUpperCase();
  if (upperUnit === 'TON' || upperUnit === 'TO' || upperUnit === 'T') {
    return 'T';
  }
  return unit;
};

export const getVolumeValueToShow = (
  field?: string | number,
  measureUnit?: EMeasureUnit,
  around = 2,
  worldCase = 'LOWERCASE'
): string => {
  if (!field && !measureUnit) {
    return '-';
  }

  const formattedUnit = formatMeasureUnit(measureUnit as string);

  const value =
    formattedUnit === EMeasureUnit.KG || formattedUnit === EMeasureUnit.T
      ? Number(field).toFixed(around).toString().replace('.', ',')
      : Number(field);

  if (isNaN(Number(field)) || Number(field) === 0) {
    return '-';
  }

  return `${value} ${
    worldCase === 'LOWERCASE'
      ? formattedUnit?.toLocaleLowerCase()
      : formattedUnit?.toUpperCase()
  }`;
};

export const ProductionProgressVolumeBar = (resume: {
  requestedSubtitle?: string;
  meltShop: {
    quantity: number;
    measureUnit: string;
  };
  rollingMill: {
    quantity: number;
    measureUnit: string;
  };
  rollingFinish: {
    quantity: number;
    measureUnit: string;
  };
  finish: {
    quantity: number;
    measureUnit: string;
  };
  stockMill: {
    quantity: number;
    measureUnit: string;
  };
  transit: {
    quantity: number;
    measureUnit: string;
  };
  stockWarehouse: {
    quantity: number;
    measureUnit: string;
  };
  requested: {
    quantity: number;
    measureUnit: string;
  };
  billed: {
    quantity: number;
    measureUnit: string;
  };
}) => {
  const getPercentage = ({
    field,
    quantityRequested
  }: {
    field: number;
    quantityRequested: number;
  }): number => {
    const requestedQuantity =
      !quantityRequested || isNaN(quantityRequested)
        ? 0
        : Number(quantityRequested);
    const fieldValue = !field || isNaN(field) ? 0 : Number(field);
    return (fieldValue / requestedQuantity) * 100;
  };

  const getValueToShow = ({
    field,
    requestedMeasureUnit,
    quantityRequested,
    measureUnit
  }: {
    field: number;
    requestedMeasureUnit: string;
    quantityRequested: number;
    measureUnit?: string;
  }): string => {
    const percentage = getPercentage({ field, quantityRequested });
    const normalizedUnit = formatMeasureUnit(measureUnit);

    const value =
      normalizedUnit === EMeasureUnit.KG || normalizedUnit === EMeasureUnit.T
        ? Number(field).toFixed(2).toString().replace('.', ',')
        : Number(field);

    return percentage > 15
      ? `${value} ${
          normalizedUnit?.toLowerCase() || requestedMeasureUnit?.toLowerCase()
        }`
      : '';
  };

  const balanceQuantity =
    (Number(resume?.requested?.quantity) || 0) -
    (Number(resume?.meltShop?.quantity || 0) +
      Number(resume?.rollingMill?.quantity || 0) +
      Number(resume?.rollingFinish?.quantity || 0) +
      Number(resume?.finish?.quantity || 0) +
      Number(resume?.stockMill?.quantity || 0) +
      Number(resume?.transit?.quantity || 0) +
      Number(resume?.stockWarehouse?.quantity || 0) +
      Number(resume?.billed?.quantity || 0));

  return (
    <ProgressVolumeBarWrapper>
      <ProgressVolumeBarContainer>
        {Number(resume?.meltShop?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.meltShop?.quantity,
                resume?.meltShop?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em Aciaria`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="meltShop"
              style={{
                width: `${getPercentage({
                  field: resume?.meltShop?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.meltShop?.quantity,
                measureUnit: resume?.meltShop?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.rollingMill?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.rollingMill?.quantity,
                resume?.rollingMill?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em Laminação`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="rollingMill"
              style={{
                width: `${getPercentage({
                  field: resume?.rollingMill?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.rollingMill?.quantity,
                measureUnit: resume?.rollingMill?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.rollingFinish?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.rollingFinish?.quantity,
                resume?.rollingFinish?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em LA`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="rollingFinish"
              style={{
                width: `${getPercentage({
                  field: resume?.rollingFinish?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.rollingFinish?.quantity,
                measureUnit: resume?.rollingFinish?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.finish?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.finish?.quantity,
                resume?.finish?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em Acabamento`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="finish"
              style={{
                width: `${getPercentage({
                  field: resume?.finish?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.finish?.quantity,
                measureUnit: resume?.finish?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.stockMill?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.stockMill?.quantity,
                resume?.stockMill?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em Estoque Usina`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="stockMill"
              style={{
                width: `${getPercentage({
                  field: resume?.stockMill?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.stockMill?.quantity,
                measureUnit: resume?.stockMill?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.transit?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.transit?.quantity,
                resume?.transit?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em Transferência`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="transit"
              style={{
                width: `${getPercentage({
                  field: resume?.transit?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.transit?.quantity,
                measureUnit: resume?.transit?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.stockWarehouse?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.stockWarehouse?.quantity,
                resume?.stockWarehouse?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em Estoque Entre-Posto`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="stockWarehouse"
              style={{
                width: `${getPercentage({
                  field: resume?.stockWarehouse?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.stockWarehouse?.quantity,
                measureUnit: resume?.stockWarehouse?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.billed?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.billed?.quantity,
                resume?.billed?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em faturado`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="billed"
              style={{
                width: `${getPercentage({
                  field: resume?.billed?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.billed?.quantity,
                measureUnit: resume?.billed?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {balanceQuantity > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                balanceQuantity,
                resume?.requested?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em saldo`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="balance"
              style={{
                width: `${getPercentage({
                  field: balanceQuantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: balanceQuantity,
                measureUnit: resume?.requested?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}
      </ProgressVolumeBarContainer>
      {!!resume.requestedSubtitle && resume.requested.quantity > 0 && (
        <RequestedQtyText>
          {resume.requestedSubtitle}{' '}
          {getVolumeValueToShow(
            resume.requested.quantity,
            resume.requested.measureUnit as EMeasureUnit,
            2
          )}
        </RequestedQtyText>
      )}
    </ProgressVolumeBarWrapper>
  );
};

export const ProgressVolumeBar = (resume: {
  requestedSubtitle?: string;
  billed: {
    quantity: number;
    measureUnit: string;
  };
  delivery: {
    quantity: number;
    measureUnit: string;
  };
  shipment: {
    quantity: number;
    measureUnit: string;
  };
  pending: {
    quantity: number;
    measureUnit: string;
  };
  requested: {
    quantity: number;
    measureUnit: string;
  };
}) => {
  const getPercentage = ({
    field,
    quantityRequested
  }: {
    field: number;
    quantityRequested: number;
  }): number => {
    const requestedQuantity =
      !quantityRequested || isNaN(quantityRequested)
        ? 0
        : Number(quantityRequested);
    const fieldValue = !field || isNaN(field) ? 0 : Number(field);
    return (fieldValue / requestedQuantity) * 100;
  };

  const getValueToShow = ({
    field,
    requestedMeasureUnit,
    quantityRequested,
    measureUnit
  }: {
    field: number;
    requestedMeasureUnit: string;
    quantityRequested: number;
    measureUnit?: string;
  }): string => {
    const percentage = getPercentage({ field, quantityRequested });
    const normalizedUnit = formatMeasureUnit(measureUnit);

    const value =
      normalizedUnit === EMeasureUnit.KG || normalizedUnit === EMeasureUnit.T
        ? Number(field).toFixed(2).toString().replace('.', ',')
        : Number(field);

    return percentage > 15
      ? `${value} ${
          normalizedUnit?.toLowerCase() || requestedMeasureUnit?.toLowerCase()
        }`
      : '';
  };

  return (
    <ProgressVolumeBarWrapper>
      <ProgressVolumeBarContainer>
        {Number(resume?.billed?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.billed?.quantity,
                resume?.billed?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} faturado`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="billed"
              style={{
                width: `${getPercentage({
                  field: resume?.billed?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.billed?.quantity,
                measureUnit: resume?.billed?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.delivery?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.delivery?.quantity,
                resume?.delivery?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em remessa`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="delivery"
              style={{
                width: `${getPercentage({
                  field: resume?.delivery?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.delivery?.quantity,
                measureUnit: resume?.delivery?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.shipment?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.shipment?.quantity,
                resume?.shipment?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} em carregamento`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="shipment"
              style={{
                width: `${getPercentage({
                  field: resume?.shipment?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.shipment?.quantity,
                measureUnit: resume?.shipment?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}

        {Number(resume?.pending?.quantity || 0) > 0 && (
          <Tooltip
            {...{
              title: `${getVolumeValueToShow(
                resume?.pending?.quantity,
                resume?.pending?.measureUnit as EMeasureUnit,
                2,
                'UPPERCASE'
              )} saldo`,
              placement: 'bottomLeft'
            }}
          >
            <div
              className="pending"
              style={{
                width: `${getPercentage({
                  field: resume?.pending?.quantity,
                  quantityRequested: resume?.requested?.quantity
                })}%`
              }}
            >
              {getValueToShow({
                field: resume?.pending?.quantity,
                measureUnit: resume?.pending?.measureUnit,
                quantityRequested: resume?.requested?.quantity,
                requestedMeasureUnit: resume?.requested?.measureUnit
              })}
            </div>
          </Tooltip>
        )}
      </ProgressVolumeBarContainer>
      {!!resume.requestedSubtitle && (
        <RequestedQtyText>
          {resume.requestedSubtitle}{' '}
          {getVolumeValueToShow(
            resume.requested.quantity,
            resume.requested.measureUnit as EMeasureUnit,
            2
          )}
        </RequestedQtyText>
      )}
    </ProgressVolumeBarWrapper>
  );
};
